import { escapeHtml } from "@/lib/sanitizeHtml";

// Helpers -------------------------------------------------------------
const BLANK = "__________";
const v = (x, blank = BLANK) => (x && String(x).trim() ? `<b>${escapeHtml(String(x).trim())}</b>` : blank);
const plain = (x, blank = BLANK) => (x && String(x).trim() ? escapeHtml(String(x).trim()) : blank);

export const fmtLongDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
};

const money = (n) => {
  if (n === "" || n == null || Number.isNaN(Number(n))) return BLANK;
  return `<b>₹ ${Number(n).toLocaleString("en-IN")}</b>`;
};

const dear = (name) => `<p>Dear ${name && name.trim() ? escapeHtml(name.trim()) : "Sir/Madam"},</p>`;

// Templates -------------------------------------------------------------
// Each template: key, label, description, heading (title printed on the letter), subject(ctx),
// fields (extra details asked for), build(ctx) -> body HTML. ctx = { ...fields, recipient_name, company }
export const LETTER_TEMPLATES = [
  {
    key: "appointment",
    acceptance: true,
    label: "Appointment Letter",
    description: "Formal appointment with joining date, salary, probation and terms.",
    heading: "APPOINTMENT LETTER",
    subject: (c) => `Appointment as ${c.designation || "____"}`,
    fields: [
      { key: "designation", label: "Designation", placeholder: "Site Engineer" },
      { key: "department", label: "Department (optional)", placeholder: "Projects" },
      { key: "joining_date", label: "Date of joining", type: "date" },
      { key: "salary", label: "Monthly salary (₹)", type: "number" },
      { key: "place", label: "Place of posting", placeholder: "Guwahati" },
      { key: "probation", label: "Probation (months)", type: "number", default: "3" },
      { key: "notice", label: "Notice period (days)", type: "number", default: "30" },
    ],
    build: (c) => `
${dear(c.recipient_name)}
<p>We are pleased to appoint you as ${v(c.designation)}${c.department ? ` in the ${plain(c.department)} department` : ""} at <b>${escapeHtml(c.company || "the Company")}</b>, on the following terms and conditions:</p>
<ol>
<li><b>Date of joining:</b> ${v(fmtLongDate(c.joining_date))}.</li>
<li><b>Place of posting:</b> ${v(c.place)}. The Company may transfer you to any of its sites or offices as the business requires.</li>
<li><b>Remuneration:</b> You will be paid a consolidated salary of ${money(c.salary)} per month, subject to applicable statutory deductions.</li>
<li><b>Probation:</b> You will be on probation for ${v(c.probation || "3")} month(s) from the date of joining. Your employment will be confirmed in writing after satisfactory completion of probation.</li>
<li><b>Working hours and leave:</b> As per the Company's policy in force from time to time.</li>
<li><b>Confidentiality:</b> You shall not disclose any confidential information, client data, rates, drawings or business matters of the Company during or after your employment.</li>
<li><b>Termination:</b> Either party may terminate this employment by giving ${v(c.notice || "30")} days' written notice or salary in lieu thereof. The Company may terminate your services without notice in case of misconduct.</li>
</ol>
<p>Please sign and return the duplicate copy of this letter as a token of your acceptance. We welcome you to the Company and wish you a successful career with us.</p>`,
  },
  {
    key: "offer",
    acceptance: true,
    label: "Offer Letter",
    description: "Job offer to a candidate, valid until a stated date.",
    heading: "OFFER LETTER",
    subject: (c) => `Offer of employment – ${c.designation || "____"}`,
    fields: [
      { key: "designation", label: "Designation", placeholder: "Accounts Executive" },
      { key: "salary", label: "Monthly salary (₹)", type: "number" },
      { key: "joining_date", label: "Expected date of joining", type: "date" },
      { key: "valid_till", label: "Offer valid until", type: "date" },
    ],
    build: (c) => `
${dear(c.recipient_name)}
<p>Further to our discussions, we are pleased to offer you the position of ${v(c.designation)} at <b>${escapeHtml(c.company || "the Company")}</b>.</p>
<ol>
<li>Your consolidated salary will be ${money(c.salary)} per month.</li>
<li>Your expected date of joining is ${v(fmtLongDate(c.joining_date))}.</li>
<li>This offer is subject to verification of your documents and references.</li>
<li>A formal appointment letter with detailed terms will be issued on joining.</li>
</ol>
<p>This offer is valid until ${v(fmtLongDate(c.valid_till))}. Kindly sign and return a copy of this letter to confirm your acceptance.</p>`,
  },
  {
    key: "confirmation",
    label: "Confirmation Letter",
    description: "Confirms an employee after successful probation.",
    heading: "CONFIRMATION LETTER",
    subject: () => "Confirmation of employment",
    fields: [
      { key: "designation", label: "Designation", placeholder: "Site Supervisor" },
      { key: "effective_date", label: "Confirmed with effect from", type: "date" },
    ],
    build: (c) => `
${dear(c.recipient_name)}
<p>We are pleased to inform you that, on completion of your probation period and a review of your performance, you are hereby confirmed in the position of ${v(c.designation)} with effect from ${v(fmtLongDate(c.effective_date))}.</p>
<p>All other terms and conditions of your appointment letter remain unchanged.</p>
<p>We appreciate your contribution and look forward to your continued commitment.</p>`,
  },
  {
    key: "increment",
    label: "Salary Revision Letter",
    description: "Increment / revised salary with effective date.",
    heading: "SALARY REVISION LETTER",
    subject: () => "Revision of salary",
    fields: [
      { key: "designation", label: "Designation", placeholder: "Project Manager" },
      { key: "old_salary", label: "Previous monthly salary (₹)", type: "number" },
      { key: "new_salary", label: "Revised monthly salary (₹)", type: "number" },
      { key: "effective_date", label: "Effective from", type: "date" },
    ],
    build: (c) => `
${dear(c.recipient_name)}
<p>In recognition of your performance and contribution as ${v(c.designation)}, the management is pleased to revise your monthly salary from ${money(c.old_salary)} to ${money(c.new_salary)} with effect from ${v(fmtLongDate(c.effective_date))}.</p>
<p>All other terms and conditions of your employment remain unchanged. Please treat this letter as confidential.</p>
<p>We wish you continued success with the Company.</p>`,
  },
  {
    key: "experience",
    label: "Experience Certificate",
    description: "Certifies a person's period of service and conduct.",
    heading: "EXPERIENCE CERTIFICATE",
    subject: () => "Experience certificate",
    toWhomItMayConcern: true,
    fields: [
      { key: "designation", label: "Designation held", placeholder: "Site Engineer" },
      { key: "from_date", label: "Employed from", type: "date" },
      { key: "to_date", label: "Employed until", type: "date" },
    ],
    build: (c) => `
<p>This is to certify that ${v(c.recipient_name)} was employed with <b>${escapeHtml(c.company || "the Company")}</b> as ${v(c.designation)} from ${v(fmtLongDate(c.from_date))} to ${v(fmtLongDate(c.to_date))}.</p>
<p>During this period, we found ${v(c.recipient_name, "the employee")} to be sincere, hardworking and professional in conduct. We wish them every success in future endeavours.</p>`,
  },
  {
    key: "relieving",
    label: "Relieving Letter",
    description: "Accepts a resignation and relieves the employee.",
    heading: "RELIEVING LETTER",
    subject: () => "Relieving from services",
    fields: [
      { key: "designation", label: "Designation", placeholder: "Accounts Executive" },
      { key: "resignation_date", label: "Date of resignation", type: "date" },
      { key: "last_day", label: "Last working day", type: "date" },
    ],
    build: (c) => `
${dear(c.recipient_name)}
<p>This is with reference to your resignation dated ${v(fmtLongDate(c.resignation_date))} from the position of ${v(c.designation)}.</p>
<p>Your resignation has been accepted and you stand relieved from the services of <b>${escapeHtml(c.company || "the Company")}</b> at the close of working hours on ${v(fmtLongDate(c.last_day))}.</p>
<p>We confirm that you have handed over all Company assets and responsibilities. We thank you for your services and wish you the very best.</p>`,
  },
  {
    key: "warning",
    acceptance: true,
    label: "Warning Letter",
    description: "Formal warning about conduct, attendance or performance.",
    heading: "WARNING LETTER",
    subject: (c) => `Warning regarding ${c.issue || "____"}`,
    fields: [
      { key: "issue", label: "Issue (short)", placeholder: "repeated absence from site" },
      { key: "incident_date", label: "Date of incident", type: "date" },
      { key: "details", label: "Details", type: "textarea", placeholder: "Describe what happened" },
    ],
    build: (c) => `
${dear(c.recipient_name)}
<p>It has been brought to our notice that on ${v(fmtLongDate(c.incident_date))} there was an issue concerning ${v(c.issue)}.</p>
<p>${c.details && c.details.trim() ? escapeHtml(c.details.trim()).replace(/\n/g, "<br>") : BLANK}</p>
<p>This conduct is not in line with the Company's standards. You are hereby <b>warned</b> to ensure that such an incident is not repeated. Any further occurrence may lead to disciplinary action, which may include termination of services.</p>
<p>Please acknowledge receipt of this letter.</p>`,
  },
  {
    key: "authority",
    label: "Authority Letter",
    description: "Authorises a person to act on the company's behalf.",
    heading: "AUTHORITY LETTER",
    subject: (c) => `Authorisation for ${c.purpose || "____"}`,
    toWhomItMayConcern: true,
    fields: [
      { key: "designation", label: "Authorised person's designation", placeholder: "Site Manager" },
      { key: "purpose", label: "Purpose", placeholder: "collecting materials from the supplier" },
      { key: "valid_till", label: "Valid until (optional)", type: "date" },
    ],
    build: (c) => `
<p>This is to authorise ${v(c.recipient_name)}${c.designation ? `, ${plain(c.designation)} of <b>${escapeHtml(c.company || "the Company")}</b>,` : ""} to act on behalf of the Company for the purpose of ${v(c.purpose)}.</p>
<p>${c.valid_till ? `This authorisation is valid until ${v(fmtLongDate(c.valid_till))}.` : "This authorisation is valid until revoked in writing."} Any action taken by the above person within the scope of this authority shall be binding on the Company.</p>`,
  },
  {
    key: "noc",
    label: "No Objection Certificate",
    description: "NOC for a purpose such as travel, higher study or visa.",
    heading: "NO OBJECTION CERTIFICATE",
    subject: () => "No Objection Certificate",
    toWhomItMayConcern: true,
    fields: [
      { key: "designation", label: "Designation", placeholder: "Engineer" },
      { key: "purpose", label: "Purpose", placeholder: "applying for a passport" },
    ],
    build: (c) => `
<p>This is to certify that ${v(c.recipient_name)} is employed with <b>${escapeHtml(c.company || "the Company")}</b> as ${v(c.designation)}.</p>
<p>The Company has <b>no objection</b> to ${v(c.recipient_name, "the employee")} ${plain(c.purpose, BLANK)}.</p>
<p>This certificate is issued on request for the above purpose only.</p>`,
  },
  {
    key: "blank",
    label: "Blank Letter",
    description: "Start from an empty page on your letterhead.",
    heading: "",
    subject: () => "",
    fields: [],
    build: () => `<p>Dear Sir/Madam,</p><p><br></p><p>Write your letter here.</p>`,
  },
];

export const getTemplate = (key) => LETTER_TEMPLATES.find((t) => t.key === key) || LETTER_TEMPLATES[LETTER_TEMPLATES.length - 1];

export function templateDefaults(tpl) {
  const out = {};
  (tpl.fields || []).forEach((f) => { out[f.key] = f.default || ""; });
  return out;
}

export function buildBody(tpl, fields, recipientName, companyName) {
  return tpl.build({ ...fields, recipient_name: recipientName, company: companyName }).trim();
}

export function buildSubject(tpl, fields) {
  return tpl.subject({ ...fields });
}
